import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import {
  Building2,
  CheckCircle2,
  FileClock,
  FileSpreadsheet,
  FileText,
  User,
  Users,
} from 'lucide-react';

import { formatarCentavos, paraCentavos } from '@/nucleo/dinheiro';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { MonthFilter } from '@/ui/MonthFilter';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import { StatusBadge } from '@/ui/StatusBadge';
import { ToggleGroup, ToggleGroupItem } from '@/ui/base/toggle-group';

import { usePersonaFechamento } from '../fechamento-v2/persona';
import { useDres } from '../hooks/useDres';
import { APRESENTACAO_DO_STATUS, competenciaPorExtenso, normalizar } from './apresentacao';
import { CicloMensalAbas } from './CicloMensalAbas';
import { useMedicoesSme } from './dados';
import { STATUS_MEDICAO_SME, type MedicaoSme, type TipoPessoaSme } from './tipos';

/**
 * Medições (SME) — `/sme/medicoes`, aba do "Ciclo mensal".
 *
 * Migrada do protótipo (`SmeMedicoes.tsx`): as medições de todas as DREs, com
 * os quatro indicadores (os três primeiros filtram a lista), busca, DRE,
 * status, competência e tipo de pessoa. Clicar numa linha abre o
 * demonstrativo daquela medição na aba vizinha.
 *
 * A LEITURA É MOCKADA. Não há módulo em `apps/api/src/modulos/` que sirva
 * medições à SME; o dado vem de `dados.ts` sob a exceção registrada em
 * `CLAUDE.md` para esta tela. A tela não tem ação de gravação.
 *
 * DIVERGÊNCIAS CONSCIENTES EM RELAÇÃO AO PROTÓTIPO:
 *
 * 1. **Sem coluna "Ações".** O protótipo termina a linha num botão
 *    "Demonstrativo" que faz o mesmo que clicar na linha. CLAUDE.md, regras de
 *    interface, item 9: a linha inteira é o alvo, e não há botão ao lado.
 * 2. **O filtro de DRE fica sempre visível** — mesma decisão de `/sme/revisoes`
 *    (CLAUDE.md, 2026-09-22): este sistema não tem escopo de DRE no `auth`.
 * 3. **A persona DRE é mandada ao painel**, como o `ProtectedSmeOnlyRoute` do
 *    protótipo faz com esta rota. A guarda de rota daqui é por área, não por
 *    tela, então a regra vive aqui dentro — e a aba nem aparece para ela.
 * 4. **Sem controles de visão de tabela** (esconder e reordenar colunas): a
 *    tabela do design system daqui não tem esse recurso em tela nenhuma.
 */

const FILTROS_PADRAO = {
  m_busca: '',
  m_mes: '',
  m_dre: '',
  m_status: '',
  m_tipo: '',
};

const OPCOES_DE_STATUS = STATUS_MEDICAO_SME.map((status) => ({
  value: status,
  label: APRESENTACAO_DO_STATUS[status].rotulo,
}));

const AGUARDANDO: readonly string[] = ['PROCESSADA', 'CALCULADA'];
const ENCERRADAS: readonly string[] = ['LIQUIDADA', 'PAGA'];

function somar(medicoes: readonly MedicaoSme[]): string {
  return formatarCentavos(medicoes.reduce((s, m) => s + (paraCentavos(m.valorTotal) ?? 0), 0));
}

export function MedicoesSmePage() {
  const persona = usePersonaFechamento();
  const navigate = useNavigate();
  const { values, setFilter, clearAll } = useUrlFilters(FILTROS_PADRAO);
  const { data: dres = [] } = useDres();
  const consulta = useMedicoesSme();

  const medicoes = useMemo(() => {
    const alvo = normalizar(values.m_busca.trim());
    return (consulta.data ?? []).filter((m) => {
      if (values.m_mes && m.mesReferencia !== values.m_mes) return false;
      if (values.m_dre && m.dre.id !== values.m_dre) return false;
      if (values.m_status && m.status !== values.m_status) return false;
      if (values.m_tipo && m.credenciado.tipo !== values.m_tipo) return false;
      if (
        alvo &&
        !normalizar(m.credenciado.nome).includes(alvo) &&
        !normalizar(m.credenciado.cpfCnpj).includes(alvo)
      ) {
        return false;
      }
      return true;
    });
  }, [consulta.data, values]);

  if (persona === 'DRE') {
    return <Navigate to="/sme" replace />;
  }

  const aguardando = medicoes.filter((m) => AGUARDANDO.includes(m.status)).length;
  const encerradas = medicoes.filter((m) => ENCERRADAS.includes(m.status)).length;

  const indicadores: StatItem[] = [
    {
      title: 'Medições',
      value: medicoes.length,
      icon: FileSpreadsheet,
      description: 'Na competência filtrada',
      onClick: () => setFilter('m_status', ''),
      active: !values.m_status,
    },
    {
      title: 'Aguardando aprovação',
      value: aguardando,
      icon: FileClock,
      description: 'Processada · Calculada',
      onClick: () => setFilter('m_status', 'PROCESSADA'),
      active: AGUARDANDO.includes(values.m_status),
    },
    {
      title: 'Liquidadas / Pagas',
      value: encerradas,
      icon: CheckCircle2,
      description: 'Encerradas no ciclo',
      onClick: () => setFilter('m_status', 'PAGA'),
      active: ENCERRADAS.includes(values.m_status),
    },
    {
      title: 'Valor total',
      value: somar(medicoes),
      icon: FileText,
      description: 'Soma da competência',
    },
  ];

  const campos: FilterFieldConfig[] = [
    {
      key: 'm_busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Credenciado ou CPF/CNPJ...',
      flex: true,
    },
    {
      key: 'm_dre',
      label: 'DRE',
      type: 'select',
      options: dres.map((d) => ({ value: d.id, label: `${d.codigo} — ${d.nome}` })),
      allOptionLabel: 'Todas as DREs',
      width: 'w-full sm:w-[220px]',
    },
    {
      key: 'm_status',
      label: 'Status',
      type: 'select',
      options: OPCOES_DE_STATUS,
      allOptionLabel: 'Todos',
      width: 'w-full sm:w-[180px]',
    },
  ];

  const colunas: DataTableColumn<MedicaoSme>[] = [
    {
      header: 'Credenciado',
      sortKey: (m) => m.credenciado.nome,
      cell: (m) => (
        <div className="flex flex-col">
          <span className="font-medium">{m.credenciado.nome}</span>
          <span className="font-mono text-xs text-muted-foreground">{m.credenciado.cpfCnpj}</span>
        </div>
      ),
    },
    {
      header: 'DRE',
      sortKey: (m) => m.dre.codigo,
      cell: (m) => (
        <span className="text-sm">
          <span className="font-medium">{m.dre.codigo}</span>{' '}
          <span className="text-muted-foreground">— {m.dre.nome}</span>
        </span>
      ),
    },
    {
      header: 'Competência',
      sortKey: (m) => m.mesReferencia,
      cell: (m) => <span>{competenciaPorExtenso(m.mesReferencia)}</span>,
    },
    {
      header: 'Termo SEI',
      cell: (m) => <span className="font-mono text-xs">{m.termoSei ?? '—'}</span>,
    },
    {
      header: 'Valor total',
      sortKey: (m) => paraCentavos(m.valorTotal),
      cell: (m) => <MoneyDisplay value={m.valorTotal} />,
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono',
    },
    {
      header: 'Status',
      sortKey: (m) => APRESENTACAO_DO_STATUS[m.status].rotulo,
      cell: (m) => (
        <StatusBadge
          status={APRESENTACAO_DO_STATUS[m.status].variante}
          label={APRESENTACAO_DO_STATUS[m.status].rotulo}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Medições"
        description="Acompanhe as medições por DRE e competência. Clique numa medição para abrir o demonstrativo."
      >
        <SeletorDeTipoDePessoa
          valor={values.m_tipo as TipoPessoaSme | ''}
          aoMudar={(tipo) => setFilter('m_tipo', tipo)}
        />
      </PageHeader>

      <CicloMensalAbas />

      <StatsGrid stats={indicadores} />

      <FilterBar
        fields={campos}
        values={values}
        defaultValues={FILTROS_PADRAO}
        onChange={setFilter}
        onClear={clearAll}
      >
        <MonthFilter
          id="m-competencia"
          value={values.m_mes}
          onChange={(mes) => setFilter('m_mes', mes)}
        />
      </FilterBar>

      {consulta.isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void consulta.refetch() }}
        />
      ) : (
        <DataTable
          columns={colunas}
          data={medicoes}
          isLoading={consulta.isLoading}
          onRowClick={(m) => navigate(`/sme/demonstrativos?medicao=${m.id}`)}
          emptyIcon={FileSpreadsheet}
          emptyTitle="Nenhuma medição encontrada"
          emptyDescription="Ajuste os filtros para visualizar medições."
          ariaLabel="Medições por credenciado e competência"
        />
      )}
    </div>
  );
}

/**
 * "Todos | PF | PJ / Coop." — o seletor do protótipo (`TipoPessoaSmeToggle`).
 * O `NaturezaToggle` do Fechamento não serve: ele não tem "Todos".
 */
function SeletorDeTipoDePessoa({
  valor,
  aoMudar,
}: {
  valor: TipoPessoaSme | '';
  aoMudar: (tipo: TipoPessoaSme | '') => void;
}) {
  return (
    <ToggleGroup
      type="single"
      value={valor || 'TODOS'}
      onValueChange={(v) => v && aoMudar(v === 'TODOS' ? '' : (v as TipoPessoaSme))}
      aria-label="Filtrar por tipo de pessoa"
    >
      <ToggleGroupItem value="TODOS" className="gap-1.5" aria-label="Todos">
        <Users className="h-4 w-4" aria-hidden="true" />
        Todos
      </ToggleGroupItem>
      <ToggleGroupItem value="PF" className="gap-1.5" aria-label="Pessoa Física">
        <User className="h-4 w-4" aria-hidden="true" />
        PF
      </ToggleGroupItem>
      <ToggleGroupItem value="PJ" className="gap-1.5" aria-label="Pessoa Jurídica e Cooperativa">
        <Building2 className="h-4 w-4" aria-hidden="true" />
        PJ / Coop.
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

export default MedicoesSmePage;
